import { DecoratorNode, NodeKey, LexicalNode, EditorConfig } from 'lexical';
import React from 'react';
import { PlaceholderTicketComponent } from './PlaceholderTicketComponent';
import { TextMatchTransformer } from '@lexical/markdown';

export type SerializedPlaceholderTicketNode = {
  title: string;
  type: 'placeholder-ticket';
  version: 1;
};

export class PlaceholderTicketNode extends DecoratorNode<React.JSX.Element> {
  __title: string;

  static getType(): string {
    return 'placeholder-ticket';
  }

  static clone(node: PlaceholderTicketNode): PlaceholderTicketNode {
    return new PlaceholderTicketNode(node.__title, node.__key);
  }

  constructor(title: string, key?: NodeKey) {
    super(key);
    this.__title = title;
  }

  exportJSON(): SerializedPlaceholderTicketNode {
    return {
      title: this.__title,
      type: 'placeholder-ticket',
      version: 1,
    };
  }

  static importJSON(
    serializedNode: SerializedPlaceholderTicketNode,
  ): PlaceholderTicketNode {
    const { title } = serializedNode;
    return $createPlaceholderTicketNode(title);
  }

  createDOM(_config: EditorConfig): HTMLElement {
    return document.createElement('span');
  }

  updateDOM(): false {
    return false;
  }

  decorate(): React.JSX.Element {
    return (
      <PlaceholderTicketComponent
        title={this.__title}
        nodeKey={this.getKey()}
      />
    );
  }

  getTitle(): string {
    return this.__title;
  }

  setTitle(title: string): void {
    const writable = this.getWritable();
    writable.__title = title;
  }
}

export function $createPlaceholderTicketNode(
  title: string,
): PlaceholderTicketNode {
  return new PlaceholderTicketNode(title);
}

export function $isPlaceholderTicketNode(
  node: LexicalNode | null | undefined,
): node is PlaceholderTicketNode {
  return node instanceof PlaceholderTicketNode;
}

/**
 * Markdown transformer for placeholder tickets
 * Format: #'Title Text Here'
 */
export const PLACEHOLDER_TICKET_TRANSFORMER: TextMatchTransformer = {
  dependencies: [PlaceholderTicketNode],
  export: (node) => {
    if (!$isPlaceholderTicketNode(node)) {
      return null;
    }

    const title = node.getTitle();
    // Escape single quotes in title
    const escapedTitle = title.replace(/'/g, "\\'");
    return `#'${escapedTitle}'`;
  },
  importRegExp: /#'((?:[^'\\]|\\.)*)'/,
  regExp: /#'((?:[^'\\]|\\.)*)'$/,
  replace: (textNode, match) => {
    const [, escapedTitle] = match;
    // Unescape single quotes
    const title = escapedTitle.replace(/\\'/g, "'");
    const placeholderNode = $createPlaceholderTicketNode(title);
    textNode.replace(placeholderNode);
  },
  trigger: "'",
  type: 'text-match',
};
